import type { MetaFunction } from "@remix-run/react"
import {
  articleBodyStyle,
  badExample,
  figCaptionStyle,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  imageGrid,
  imagePreview2,
  stepSectionStyle,
} from "./article.css"
import Footer from "~/components/Footer"
import { golden } from "~/components/styles/theme.css"
import ScrollyWolly from "~/components/ScrollyWolly"
import LinkButton from "~/components/LinkButton"
import { page } from "~/shared/styles.css"
import Logo from "~/components/Logo"
import StickyHeader from "~/components/StickyHeader"
import Link from "~/components/Link"

export type PreviewImage = {
  url: string
  size: { w: number; h: number }
}

export type Url = { url: string; initial?: PreviewImage | null; loading?: boolean }

export const meta: MetaFunction = () => {
  return [
    {
      title: "Contemporary Garden Ideas | Effortlessly Design Your Space",
    },
    {
      name: "description",
      content:
        "Contemporary garden ideas with an AI garden design tool. Create a contemporary escape using Rescape AI. It's easy and quick to visualise styles in your space.",
    },
    {
      property: "og:title",
      content: "How to design a contemporary garden with Rescape AI",
    },
    {
      property: "og:description",
      content:
        "It's actually very easy. This guide walks you through the entire process and demonstrates the power of Rescape AI.",
    },
    {
      property: "og:image",
      content:
        "https://rescapeai.com/how-to-design-a-contemporary-garden/third-ai-garden-design-with-stone-paving-area.webp",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/article/contemporary-garden-ideas-with-rescape-ai",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "Contemporary Garden Ideas | Effortlessly Design Your Space",
        description:
          "Contemporary garden ideas with an AI garden design tool. Create a contemporary escape using Rescape AI. It's easy and quick to visualise styles in your space.",
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2024-04-07",
        dateModified: "2024-04-07",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image:
          "https://rescapeai.com/how-to-design-a-contemporary-garden/fourth-ai-garden-design-with-stone-paving-area.webp",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id":
            "https://rescapeai.com/article/contemporary-garden-ideas-with-rescape-ai",
        },
        articleSection: [
          "Gardening",
          "Contemporary Design",
          "Artificial Intelligence",
          "Technology",
        ],
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage:
            "url(/how-to-design-a-contemporary-garden/fourth-ai-garden-design-with-stone-paving-area.webp)",
          backgroundPosition: "20%",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            Create a Contemporary Garden with AI Garden Design
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <ScrollyWolly
          before={{
            image: "/how-to-design-a-contemporary-garden/input-photo-of-your-garden.webp",
            title: "Before",
            alt: "A customers garden before using the Rescape AI platform to generate garden ideas.",
          }}
          after={{
            image:
              "/how-to-design-a-contemporary-garden/first-ai-garden-design-with-stone-paving-area.webp",
            title: "After",
            alt: "An exquisite example of generative AI garden ideas. A wooden pergola stands tall, draped in climbing plants with a comfortable looking contemporary bench nestled within.",
          }}
          height="478px"
        />
        <section className={stepSectionStyle}>
          <p className={articleBodyStyle}>
            It&apos;s now possible to generate a contemporary garden design of your
            outdoor space using ai. Using the garden design tool from Rescape AI this
            guide will show you how you can experiment and generate realistic variations
            of your outdoor space. What constitutes a Contemporary garden?{" "}
            <Link href="/garden-design-guide/contemporary/features-of-contemporary-gardens">
              That's explained here{" "}
            </Link>
            For now, we're focusing on the process of generating that style from pictures
            of your current garden
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h2Style}>Step One</h3>
          <p className={articleBodyStyle}>
            Firstly, take a clear high quality photo of your garden. Android and iPhone
            cameras work great for this but a higher quality input images mean higher
            quality designs.
          </p>
          <p className={articleBodyStyle}>
            Try to capture your image on a bright clear day. Your image can be portrait or
            landscape, whatever captures your space better.
          </p>

          <p
            className={h3Style}
            style={{ marginTop: `${golden * 2}rem`, textAlign: "center" }}
          >
            Take your photo carefully
          </p>
          <p
            className={articleBodyStyle}
            style={{
              fontSize: "16px",
              textAlign: "center",
              lineHeight: "1.25rem",
              marginTop: ".25rem",
            }}
          >
            Here are some examples of bad input images
          </p>
          <div className={imageGrid}>
            <div className={imagePreview2}>
              <img
                src="/how-to-design-a-contemporary-garden/bad-examples/blurry-input-image-example.webp"
                alt="A bad example because it's blurry"
                style={{ borderRadius: "4px", overflow: "hidden" }}
                loading="lazy"
              />
              <div className={badExample} />
              <figcaption className={figCaptionStyle}>
                Do not use an out of focus input photo
              </figcaption>
            </div>
            <div className={imagePreview2}>
              <img
                src="/how-to-design-a-contemporary-garden/bad-examples/camera-shake-input-image-example.webp"
                alt="A bad example because it has camera shake"
                style={{ borderRadius: "4px", overflow: "hidden" }}
                loading="lazy"
              />
              <div className={badExample} />
              <figcaption className={figCaptionStyle}>
                Do not use photos with camera shake
              </figcaption>
            </div>
            <div className={imagePreview2}>
              <img
                src="/how-to-design-a-contemporary-garden/bad-examples/grainy-input-image-example.webp"
                alt="A bad example because there is too much grain"
                style={{ borderRadius: "4px", overflow: "hidden" }}
                loading="lazy"
              />
              <div className={badExample} />
              <figcaption className={figCaptionStyle}>
                Do not use grainy photos
              </figcaption>
            </div>
            <div className={imagePreview2}>
              <img
                src="/how-to-design-a-contemporary-garden/bad-examples/under-exposed-input-image-example.webp"
                alt="A bad example because it is under exposed"
                style={{ borderRadius: "4px", overflow: "hidden" }}
                loading="lazy"
              />
              <div className={badExample} />
              <figcaption className={figCaptionStyle}>
                Do not use poorly exposed photos
              </figcaption>
            </div>
          </div>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h2Style}>Here is an ideal input photo</h3>
          <img
            src="/how-to-design-a-contemporary-garden/input-photo-of-your-garden.webp"
            alt="An ideal input example, it's sharp, focused, and well lit."
            style={{ borderRadius: "8px", overflow: "hidden", marginTop: `${golden}rem` }}
            loading="lazy"
          />
          <figcaption className={figCaptionStyle}>
            Focused, sharp, clear and bright garden photos like above work best.
          </figcaption>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h2Style}>Step Two</h3>
          <p className={articleBodyStyle}>
            Select the image and choose &apos;contemporary&apos; from the style choices in
            the form. For this guide we are going to choose the feature &apos;pegola with
            relaxing seating area&apos;.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h2Style}>Step Three</h3>
          <p className={articleBodyStyle}>
            We can match the input image even more closely by telling the AI what areas we
            do not want changed. We do this by masking areas of the image to preserve.
          </p>
          <p className={articleBodyStyle}>
            Open the crop and mask menu that shows when you&apos;ve uploaded an image.
            When you&apos;re done with the crop on the next screen use the brush and paint
            the area you&apos;d like to preserve.
          </p>
          <p className={articleBodyStyle}>
            On this example I want to preserve the shed and the background because I want
            it to match my environment exactly.
          </p>
          <p className={articleBodyStyle}>
            I&apos;m going to leave some space for the pegola to be generated as it will
            be higher than the fence so my mask ends up like this.
          </p>
          <img
            src="/how-to-design-a-contemporary-garden/first-mask-to-instruct-ai-garden-generator.webp"
            alt="A demonstration of the mask to help guide the AI's generative capabilities"
            style={{
              borderRadius: "8px",
              overflow: "hidden",
              marginTop: `${golden * 2}rem`,
            }}
            loading="lazy"
          />
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h2Style}>Step Four</h3>
          <p className={articleBodyStyle}>
            A vital part of the design process is selecting the creativity, lower
            creativity means that less of the input photo will be changed whereas
            creativity of 100% will change everything in the garden.
          </p>
          <p className={articleBodyStyle}>
            There is fine grained control available here but usually 10% increments are
            enough. The default creativity is 42% which works nicely a lot of the time so
            I'm keeping it at that. Some styles require more or less creativity. If you
            need to drastically change an area, select maximum creativity.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h3 className={h2Style}>Step Five</h3>
          <p className={articleBodyStyle}>
            Choose up to four generations at a time, the output of each generated image
            will be different. I selected three and these are my outputs..
          </p>
          <img
            src="/how-to-design-a-contemporary-garden/first-revision-ai-generated-garden.webp"
            alt="Version one of the Contemporary garden with 'pergola with comfortable seating area' prompt. This modern pergola features a sleek design, surrounded by abundant plants that enhance the feeling of being immersed in nature."
            style={{
              borderRadius: "8px",
              overflow: "hidden",
              marginTop: `${golden * 2}rem`,
            }}
            loading="lazy"
          />
          <img
            src="/how-to-design-a-contemporary-garden/second-revision-ai-generated-garden.webp"
            alt="Version two of the Contemporary garden with 'pergola with comfortable seating area' prompt. This design features a formal painted metal pergola paired with comfortable wooden seating and has a smaller quantity of plants and flowers."
            style={{
              borderRadius: "8px",
              overflow: "hidden",
              marginTop: `${golden * 2}rem`,
            }}
            loading="lazy"
          />
          <img
            src="/how-to-design-a-contemporary-garden/third-revision-ai-generated-garden.webp"
            alt="Version three of the Contemporary garden with 'pergola with comfortable seating area' prompt. An open pergola, draped in leafy climbing plants and an superb white and pine seating area."
            style={{
              borderRadius: "8px",
              overflow: "hidden",
              marginTop: `${golden * 2}rem`,
            }}
            loading="lazy"
          />
        </section>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <LinkButton url="/hello/signup" shouldPrefetch>
            Join now
          </LinkButton>
        </div>
        <section className={stepSectionStyle}>
          <h3 className={h2Style}>Step Six</h3>
          <p className={articleBodyStyle}>
            I really like the first output so I&apos;m going to select it for another pass
            through the AI. There&apos;s a lot of grass but really I want a patio infront
            of the pegola. This time I&apos;m going to mask the whole image apart from
            where I want the patio, I will select the Stone paving area feature and I'm
            going to boost creativity to 100%
          </p>
          <p className={articleBodyStyle}>Here are the final renders from the model</p>
          <div className={imagePreview2}>
            <img
              src="/how-to-design-a-contemporary-garden/first-ai-garden-design-with-stone-paving-area.webp"
              alt="The version one contemporary garden idea remixed a second time to create an attractive pathway and complementary plants."
              style={{
                borderRadius: "8px",
                overflow: "hidden",
                marginTop: `${golden * 2}rem`,
              }}
              loading="lazy"
            />
            <img
              src="/how-to-design-a-contemporary-garden/second-ai-garden-design-with-stone-paving-area.webp"
              alt="The version one contemporary garden idea remixed with a diagonal patio area, creating contrast and zones for relaxation and movement within the garden."
              style={{
                borderRadius: "8px",
                overflow: "hidden",
                marginTop: `${golden * 2}rem`,
              }}
              loading="lazy"
            />
            <img
              src="/how-to-design-a-contemporary-garden/third-ai-garden-design-with-stone-paving-area.webp"
              alt="Version one of the contemporary garden idea remixed to include a permeable patio surrounded by low maintenance shrubs marking an entry way to the relaxing pergola seating area."
              style={{
                borderRadius: "8px",
                overflow: "hidden",
                marginTop: `${golden * 2}rem`,
              }}
              loading="lazy"
            />
            <img
              src="/how-to-design-a-contemporary-garden/fourth-ai-garden-design-with-stone-paving-area.webp"
              alt="Another contemporary garden idea remixed this time with asymmetric paving, creating a striking contrast and unique configuration specific to this garden."
              style={{
                borderRadius: "8px",
                overflow: "hidden",
                marginTop: `${golden * 2}rem`,
              }}
              loading="lazy"
            />
          </div>
        </section>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <LinkButton url="/hello/signup" shouldPrefetch>
            Get started
          </LinkButton>
        </div>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>You've just designed your garden with AI!</h2>
          <p className={articleBodyStyle}>
            There you have it. Follow these simple steps with your garden to generate your
            own ideas, there are fifteen base styles to choose from with many features for
            each style.
          </p>
        </section>
        <Footer />
      </div>
    </>
  )
}

export default Index
